import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from 'components/AuthorizationComponent';
import { WarningDialog } from 'components/WarningDialog';
import { HYPER_MONITORING_TYPES, hyperMonitoringTypes } from 'constants/hyper-monitoring.constants';
import { DEFAULT_PAGE_SIZE } from 'constants/pagination.constants';
import { permissionEnums } from 'constants/permission.constants';
import { pagesEnum } from 'constants/ui.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { HyperMonitoringTables } from 'sections/HyperMonitoring/HyperMonitoringTables';
import { getHyperMonitoringOperations } from 'services/hyper-monitoring.service';
import { getToken } from 'state/reducers/auth.reducers';
import { clearError, clearRequests, getHyperMonitoringError } from 'state/reducers/hyper-monitoring.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { Page } from '../theme/elements/Page';

export const HyperMonitoringApp = () => {
	const { type } = useParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const error = useAppSelector(getHyperMonitoringError);
	const token = useAppSelector(getToken);
	const { permissions } = useAppSelector(getUserDetails);
	const permissionList = Array.isArray(permissions) ? permissions : permissions?.permissions;
	const canView = hasPermission([permissionEnums.VIEW_HYPER_MONITORING]);

	const [isInitialLoading, setIsInitialLoading] = useState(!type);
	const [pages, setPages] = useState<Record<string, number>>({});

	const visibleTypes = useMemo(() => {
		if (!type) {
			return [...HYPER_MONITORING_TYPES];
		}

		return HYPER_MONITORING_TYPES.filter((requestType) => requestType === type);
	}, [type]);

	const fetchRequestsByType = useCallback(
		async (requestType: hyperMonitoringTypes, page: number) => {
			setPages((prev) => ({ ...prev, [requestType]: page }));

			if (requestType === hyperMonitoringTypes.BENEFICIARIES) {
				await dispatch(
					getHyperMonitoringOperations({
						offset: page * DEFAULT_PAGE_SIZE,
						limit: DEFAULT_PAGE_SIZE,
					})
				);
			}
		},
		[dispatch]
	);

	const fetchData = useCallback(async () => {
		const shouldShowGlobalLoader = !type;
		if (shouldShowGlobalLoader) {
			setIsInitialLoading(true);
		}

		try {
			await Promise.all(visibleTypes.map((requestType) => fetchRequestsByType(requestType, 0)));
		} finally {
			if (shouldShowGlobalLoader) {
				setIsInitialLoading(false);
			}
		}
	}, [type, visibleTypes, fetchRequestsByType]);

	useEffect(() => {
		if (token && (!permissionList || permissionList.length === 0)) {
			return;
		}
		if (!canView) {
			navigate(`/${pagesEnum.PAGE_NOT_FOUND}`);
			return;
		}
		fetchData();
	}, [token, permissionList, canView, navigate, fetchData]);

	useEffect(() => {
		return () => {
			dispatch(clearRequests());
		};
	}, [dispatch]);

	const handlePageChange = useCallback(
		async (requestType: hyperMonitoringTypes, page: number) => {
			await fetchRequestsByType(requestType, page);
		},
		[fetchRequestsByType]
	);

	if (!canView) {
		return null;
	}

	return (
		<Page
			title={t('page.hyperMonitoring.title')}
			subTitle={t('page.hyperMonitoring.subtitle')}
			loading={isInitialLoading}
		>
			<WarningDialog
				open={error.exist}
				subTitle={error.text ? t(error.text) : ''}
				handleCancel={() => dispatch(clearError())}
			/>
			<HyperMonitoringTables
				visibleTypes={visibleTypes}
				pages={pages}
				onPageChange={handlePageChange}
				perPage={DEFAULT_PAGE_SIZE}
			/>
		</Page>
	);
};
